import {Add} from '@mui/icons-material'
import {Box, Typography, Alert, Button} from '@mui/material'
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {SplitLoadMaterial} from '../types'
import {
  QuantityValidationResult,
  calculateMinQuantityForMaterial
} from '../utils/quantityValidation'

import {MaterialRow} from './MaterialRow'

interface SplitLoadMaterialListProps {
  selectedMaterials: SplitLoadMaterial[]
  availableMaterials: SplitLoadMaterial[]
  validationResult: QuantityValidationResult
  hasSourceSelected: boolean
  onUpdateMaterial: (index: number, newMaterial: SplitLoadMaterial) => void
  onRemoveMaterial: (index: number) => void
  onAddMaterial: (material: SplitLoadMaterial) => void
  'data-test-id'?: string
}

export const SplitLoadMaterialList: React.FC<SplitLoadMaterialListProps> = ({
  selectedMaterials,
  availableMaterials,
  validationResult,
  hasSourceSelected,
  onUpdateMaterial,
  onRemoveMaterial,
  onAddMaterial,
  'data-test-id': dataTestId
}) => {
  const {t} = useTranslation()

  const handleAddMaterialClick = useCallback(() => {
    if (availableMaterials.length > 0 && validationResult.canAddMaterial) {
      onAddMaterial(availableMaterials[0])
    }
  }, [availableMaterials, validationResult.canAddMaterial, onAddMaterial])

  return (
    <Box data-test-id={dataTestId}>
      {/* Section header */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 2}}>
        <Typography variant="h3" color="text.secondary">
          {t('orderIntake.splitLoad.materials')}
        </Typography>
      </Box>

      {/* Material Rows - Display directly when packaging is selected */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
        {selectedMaterials.length === 0 ? (
          // Empty state
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4
            }}
            data-test-id="split-load-material-list-empty-state"
          >
            {hasSourceSelected && availableMaterials.length === 0 ? (
              <Alert
                severity="warning"
                sx={{width: '100%'}}
                data-test-id="split-load-material-list-no-valid-materials-alert"
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 400
                  }}
                >
                  {t('orderIntake.splitLoad.noValidMaterialsForSource')}
                </Typography>
              </Alert>
            ) : (
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '15px',
                  fontWeight: 400,
                  color: '#52667a',
                  textAlign: 'center',
                  maxWidth: 250
                }}
                data-test-id="split-load-material-list-empty-state-helper-text"
              >
                {t('orderIntake.splitLoad.selectMaterialSourceHelper')}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {selectedMaterials.map((material, index) => (
              <MaterialRow
                key={`${material.materialNumber}-${index}`}
                material={material}
                index={index}
                availableMaterials={availableMaterials}
                showDelete={index >= 2}
                hasPalletSize={Boolean(material.bomMaterialNumber)}
                maxQuantity={validationResult.maxCapacity}
                minQuantity={calculateMinQuantityForMaterial(index, selectedMaterials)}
                canIncreaseQuantity={validationResult.canIncreaseQuantity}
                canDecreaseQuantity={validationResult.canDecreaseQuantity}
                onUpdate={onUpdateMaterial}
                onDelete={onRemoveMaterial}
                data-test-id={`split-load-material-row-${index}`}
              />
            ))}

            {/* Validation warnings */}
            {validationResult.warningMessageKey && (
              <Alert
                severity={
                  validationResult.isAboveMax || validationResult.isBelowMin ? 'error' : 'warning'
                }
                sx={{mt: 1}}
                data-test-id="split-load-material-list-validation-warning"
              >
                {t(validationResult.warningMessageKey, validationResult.warningMessageParams || {})}
              </Alert>
            )}

            {/* Helper text about capacity */}
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontSize: '12px',
                fontWeight: 400,
                color: '#52667a'
              }}
              data-test-id="split-load-material-list-capacity-info"
            >
              {t('orderIntake.splitLoad.capacityInfo', {
                total: validationResult.totalQuantity,
                max: validationResult.maxCapacity,
                min: validationResult.minCapacity
              })}
            </Typography>

            {/* Add Material Button */}
            {
              <Box>
                <Button
                  variant="text"
                  startIcon={<Add />}
                  disabled={!validationResult.canAddMaterial && validationResult.isAtMax}
                  onClick={handleAddMaterialClick}
                  sx={{
                    color: '#016ad4',
                    cursor: 'pointer',
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 500,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                  data-test-id="split-load-material-list-add-material-button"
                >
                  {t('orderIntake.splitLoad.addMaterialButton')}
                </Button>
              </Box>
            }
          </>
        )}
      </Box>
    </Box>
  )
}
